<script setup>
import { computed, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);

const isGroup = computed(() => {
  const sender = currentChat.value?.meta?.sender;
  if (!sender) return false;

  const senderId = sender.id;
  const contact = senderId
    ? store.getters['contacts/getContact'](senderId)
    : null;
  const isEvolutionGroup = contact?.additional_attributes?.evolution_group;
  const senderIdentifier = sender.identifier;

  return !!(
    (senderIdentifier && senderIdentifier.endsWith('@g.us')) ||
    isEvolutionGroup
  );
});

const isTranscribeEnabled = computed(() => {
  const transcribeAudio =
    currentChat.value.additional_attributes?.transcribe_audio;
  if (transcribeAudio !== undefined && transcribeAudio !== null) {
    return transcribeAudio === true;
  }
  return !isGroup.value;
});

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  if (!currentChat.value.is_pinned) {
    items.push({
      icon: 'i-lucide-pin',
      label: t('CONTACT_PANEL.PIN_CONVERSATION'),
      action: 'pin',
      value: 'pin',
    });
  } else {
    items.push({
      icon: 'i-lucide-pin-off',
      label: t('CONTACT_PANEL.UNPIN_CONVERSATION'),
      action: 'unpin',
      value: 'unpin',
    });
  }

  if (!isTranscribeEnabled.value) {
    items.push({
      icon: 'i-lucide-audio-lines',
      label: t('CONTACT_PANEL.ENABLE_TRANSCRIBE_AUDIO'),
      action: 'enable_transcribe',
      value: 'enable_transcribe',
    });
  } else {
    items.push({
      icon: 'i-lucide-audio-lines',
      label: t('CONTACT_PANEL.DISABLE_TRANSCRIBE_AUDIO'),
      action: 'disable_transcribe',
      value: 'disable_transcribe',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return items;
});

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'pin') {
    store.dispatch('pinConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.PINNED_SUCCESS'));
  } else if (action === 'unpin') {
    store.dispatch('unpinConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNPINNED_SUCCESS'));
  } else if (action === 'enable_transcribe') {
    store.dispatch('toggleTranscribeAudio', {
      conversationId: currentChat.value.id,
      transcribeAudio: true,
    });
    useAlert(t('CONTACT_PANEL.TRANSCRIBE_AUDIO_ENABLED'));
  } else if (action === 'disable_transcribe') {
    store.dispatch('toggleTranscribeAudio', {
      conversationId: currentChat.value.id,
      transcribeAudio: false,
    });
    useAlert(t('CONTACT_PANEL.TRANSCRIBE_AUDIO_DISABLED'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-brand/5"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
